import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpIcon, InformationCircleIcon } from "@heroicons/react/outline";

export interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    tooltip?: string;
  };
  /** Content shown on card hover (alternative to trend) */
  hoverContent?: ReactNode;
  className?: string;
  href?: string;
}

const StatsCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  hoverContent,
  className = "",
  href,
}: StatsCardProps) => {
  const valueString = String(value);

  const cardContent = (
    <div
      className={`group/card bg-white rounded-xl border border-gray-200 p-3 shadow-sm hover:shadow-md transition-all duration-200 h-full ${
        href ? "cursor-pointer" : ""
      } ${className}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 mb-0.5">{title}</p>
          <p className="text-xl font-bold text-gray-900 truncate" title={valueString}>
            {value}
          </p>
          <div className="h-7 mt-1">
            {trend || hoverContent ? (
              <div className="opacity-0 group-hover/card:opacity-100 transition-opacity duration-200 ease-out">
                {trend ? (
                  <div className="flex items-center gap-1.5 pt-1 group/trend relative">
                    <ArrowUpIcon
                      className={`h-4 w-4 flex-shrink-0 ${
                        trend.isPositive ? "text-green-600" : "text-red-600 rotate-180"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium flex-shrink-0 ${
                        trend.isPositive ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {trend.isPositive ? "+" : "-"}
                      {trend.value}%
                    </span>
                    {subtitle && <span className="text-sm text-gray-400 truncate">{subtitle}</span>}
                    {trend.tooltip && (
                      <span title={trend.tooltip} className="cursor-help flex-shrink-0">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="pt-1 text-sm text-gray-500 truncate">{hoverContent}</div>
                )}
              </div>
            ) : (
              subtitle && <p className="text-sm text-gray-500 truncate">{subtitle}</p>
            )}
          </div>
        </div>
        {icon && <div className="p-2 bg-green-100 rounded-lg text-green-600">{icon}</div>}
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{cardContent}</Link>;
  }

  return cardContent;
};

export default StatsCard;
